import { Column } from '@hogwarts/ui-components-grid';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { GridView } from '../../../../components';
import {
  gridOptions as defaultGridOptions,
} from '../../../profilesGridUtils';

const gridOptions = {
  ...defaultGridOptions,
  rowSelection: 'single',
  sideBar: false,
  sizeColumnsToFit: true,
  defaultColDef: {
    ...defaultGridOptions.defaultColDef,
    width: 100,
    filter: true,
    editable: false,
    suppressMenu: true,
  },
  suppressRowClickSelection: true,
  suppressCellSelection: true,
};

interface DocumentManifestRow {
  locationName: string;
  userId: string;
  documentId: string;
  filename: string;
}

interface SummaryRow {
  locationName: string;
  fileCount: number;
}

const buildSummaryRows = (rows: DocumentManifestRow[]): SummaryRow[] => {
  const counts = new Map<string, number>();
  for (const row of rows) {
    counts.set(row.locationName, (counts.get(row.locationName) || 0) + 1);
  }

  return Array.from(counts.entries())
    .sort(([a], [b]) => a.localeCompare(b))
    .map(([locationName, fileCount]) => ({ locationName, fileCount }));
};

const DocumentManifestGridView = ({
  rows,
  view,
}: {
  rows: DocumentManifestRow[];
  view: 'detail' | 'summary';
}) => {
  const { t } = useTranslation();

  const detailColumns: Column[] = useMemo(
    () => [
      {
        key: 'locationName',
        label: t('Location'),
        width: 280,
      },
      {
        key: 'userId',
        label: t('User ID'),
        width: 160,
      },
      {
        key: 'documentId',
        label: t('Document ID'),
        width: 220,
      },
      {
        key: 'filename',
        label: t('Filename'),
        width: 400,
      },
    ],
    [t]
  );

  const summaryColumns: Column[] = useMemo(
    () => [
      {
        key: 'locationName',
        label: t('Location'),
        width: 400,
      },
      {
        key: 'fileCount',
        label: t('File Count'),
        width: 120,
      },
    ],
    [t]
  );

  const summaryRows = useMemo(() => buildSummaryRows(rows || []), [rows]);

  if (view === 'summary') {
    return (
      <div className="h-100">
        <GridView
          {...gridOptions}
          getRowNodeId={(data: SummaryRow) => data.locationName}
          groups={[]}
          columns={summaryColumns}
          rows={summaryRows}
          showSideBar={false}
        />
      </div>
    );
  }

  return (
    <div className="h-100">
      <GridView
        {...gridOptions}
        getRowNodeId={(data: DocumentManifestRow) => data.documentId}
        groups={[]}
        columns={detailColumns}
        rows={rows}
        showSideBar={false}
      />
    </div>
  );
};

export default DocumentManifestGridView;
